import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  AccountManager,
  BonusApi,
  Bus,
  IntlManager,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { bonusesAvailableRoutes, clientProfileRoutes, COMPONENT_TYPES } from '@atlas/routing';
import { currencySymbols, FreeBetNotification, Label } from '@atlas/ui';

import { bonusStatuses, HIDE_FREEBET_FOR_CURRENT_SESSION } from 'common/constants';

const FreeBetNotificationCard = () => {
  const { lang: language } = useSettings();
  const [items, setItems] = useState([]);
  const currency = AccountManager.getAccountCurrency();
  const { authenticated } = useApplicationState();
  const [freeBetsCount, setFreeBetsCount] = useState(0);
  const [hideFreeBetForCurrentSession, setHideFreeBetForCurrentSession] = useState(
    () => StorageManager.getItem(HIDE_FREEBET_FOR_CURRENT_SESSION) === 'true',
  );

  const getFreeBetBonuses = useCallback(
    (currency, language) => {
      if (authenticated && !hideFreeBetForCurrentSession) {
        BonusApi.getFreebetBonuses({ currency, language }).then(data => {
          const result = data.freeBets
            .filter(item => item.status === bonusStatuses.ACTIVATED)
            .map(({ currency, expirationTime, freeBet, freeBetAmount, id, status }) => ({
              amount: {
                currency: currencySymbols[currency] || '',
                value: freeBetAmount && IntlManager.formatMoney(freeBetAmount),
              },
              description: freeBet.description,
              expirationDate: expirationTime,
              id,
              name: freeBet.name,
              status,
            }));
          setItems(result);
          setFreeBetsCount(result.length);
        });
      }
    },
    [authenticated, hideFreeBetForCurrentSession],
  );

  const loginHandler = useCallback(() => {
    setHideFreeBetForCurrentSession(false);
    getFreeBetBonuses(currency, language);
  }, [currency, getFreeBetBonuses, language]);

  useEffect(() => {
    Bus.on(Bus.events.auth.login, loginHandler);

    return () => {
      Bus.off(Bus.events.auth.login, loginHandler);
    };
  }, [loginHandler]);

  useEffect(() => getFreeBetBonuses(currency, language), [currency, getFreeBetBonuses, language]);

  const showAllButton = freeBetsCount > 2;

  const { CLIENT_PROFILE } = COMPONENT_TYPES;
  const clientProfilePath = `${SiteConfigManager.getComponentPath(CLIENT_PROFILE)}/${
    clientProfileRoutes.bonuses
  }/${bonusesAvailableRoutes.bonusesFreebet.link}`;
  const { push: historyPush } = useHistory();

  const onListClick = useCallback(() => {
    historyPush(clientProfilePath);
  }, [historyPush, clientProfilePath]);

  const onReminderClick = () => {
    StorageManager.setItem(HIDE_FREEBET_FOR_CURRENT_SESSION, 'true');
    setHideFreeBetForCurrentSession(true);
  };

  const activatedItems = useMemo(
    () => items?.filter(item => item.status === bonusStatuses.ACTIVATED),
    [items],
  );

  const freebetAmount = useMemo(() => activatedItems?.length || 0, [activatedItems]);

  const displayItems = useMemo(() => activatedItems?.slice(0, 2), [activatedItems]);

  const isFreeBetVisible = !!(
    displayItems.length &&
    authenticated &&
    !hideFreeBetForCurrentSession
  );

  useEffect(() => {
    const getFreeBetBonusesHandler = () => {
      if (currency && language) {
        getFreeBetBonuses(currency, language);
      }
    };

    Bus.on(Bus.events.bet.placedSuccess, getFreeBetBonusesHandler);

    return () => Bus.off(Bus.events.bet.placedSuccess, getFreeBetBonusesHandler);
  }, [currency, getFreeBetBonuses, language]);

  return (
    <>
      {isFreeBetVisible && (
        <div className="freebet-notification-wrapper">
          <FreeBetNotification
            freebetAmount={freebetAmount}
            items={displayItems}
            list={
              showAllButton && {
                label: 'freebet_notification_all',
                onClick: onListClick,
                params: { freebetAmount },
              }
            }
            reminder={{
              label: 'remind_me_later',
              onClick: onReminderClick,
            }}
            title="freebet_notification_title"
          >
            <Label message="freebet_notification_info" />
          </FreeBetNotification>
        </div>
      )}
    </>
  );
};
export default FreeBetNotificationCard;
